import type { ReactNode } from 'react'
import type { ScanBarState } from '../shell/scanFocus'
import { ScanBar, type CameraState } from './ScanBar'

/**
 * Every station screen is this shell: quiet header at the top out of the thumb
 * arc, the work in the middle, the scan bar docked at the bottom where the hand
 * already is (ui-design-system.md 10.2).
 *
 * The sync line is deliberately visible rather than reassuring. An operator whose
 * drafts are queueing should know it now, not discover it at the end of a session.
 */
export interface UndoOffer {
  label: string
  secondsLeft: number
}

export interface StationShellProps {
  title: string
  /** The throughput fragment - "18 catalogued today". Mary works against this number. */
  progress?: string
  queue?: number
  scan: ScanBarState
  camera?: CameraState
  /** From useCamera. The shell only carries it down to the bar; it owns no stream. */
  videoRef?: React.Ref<HTMLVideoElement>
  onCapturePhoto?: () => void
  pendingDrafts?: number
  online?: boolean
  undo?: UndoOffer
  children: ReactNode
}

export function StationShell({
  title,
  progress,
  queue,
  scan,
  camera,
  videoRef,
  onCapturePhoto,
  pendingDrafts = 0,
  online = true,
  undo,
  children,
}: StationShellProps) {
  return (
    <div className="station">
      <header className="station-header">
        <h1 className="t-display">{title}</h1>
        {progress ? <p className="t-label muted">{progress}</p> : null}
        {queue === undefined ? null : (
          <span className="chip chip-queue" aria-label="queue">
            {queue}
          </span>
        )}
      </header>

      {pendingDrafts > 0 || !online ? (
        <p className={`sync-line${online ? '' : ' sync-offline'}`} aria-live="polite">
          {online ? 'Syncing' : 'Working offline'}
          {pendingDrafts > 0 ? ` - ${pendingDrafts} waiting to sync` : ''}
        </p>
      ) : null}

      <main className="station-body">{children}</main>

      {undo ? (
        <button type="button" className="undo-pill">
          {undo.label} - {undo.secondsLeft}s
        </button>
      ) : null}

      <ScanBar state={scan} camera={camera} videoRef={videoRef} onCapturePhoto={onCapturePhoto} />
    </div>
  )
}
